import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Outlet, createBrowserRouter } from 'react-router-dom';
import { AppShell } from '@/app/layout/AppShell';
import { isAuthenticated, useAuthStore } from '@/stores/auth.store';
import { ListSkeleton } from '@/components/shared/Skeletons';

const HomePage = lazy(() => import('@/pages/HomePage'));
const MembersPage = lazy(() => import('@/pages/MembersPage'));
const MemberDetailPage = lazy(() => import('@/pages/MemberDetailPage'));
const CapturePage = lazy(() => import('@/pages/CapturePage'));
const ArchivePage = lazy(() => import('@/pages/ArchivePage'));
const DocumentDetailPage = lazy(() => import('@/pages/DocumentDetailPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const CompleteProfilePage = lazy(() => import('@/pages/CompleteProfilePage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function SuspenseFallback() {
  return (
    <div className="pt-4">
      <ListSkeleton />
    </div>
  );
}

function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<SuspenseFallback />}>{children}</Suspense>;
}

/**
 * Route guards (Phase F1 — Identity).
 *
 * - Unauthenticated -> `/login`.
 * - Authenticated but `!isProfileComplete` -> `/complete-profile` (blocking).
 * - Authenticated + complete -> app routes.
 */
export function RequireAuth() {
  const user = useAuthStore((s) => s.user);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (user && !user.isProfileComplete) {
    return <Navigate to="/complete-profile" replace />;
  }
  return <Outlet />;
}

export function RedirectIfAuthenticated() {
  const user = useAuthStore((s) => s.user);

  if (isAuthenticated()) {
    return <Navigate to={user && !user.isProfileComplete ? '/complete-profile' : '/'} replace />;
  }
  return <Outlet />;
}

/** Gated on being authenticated AND profile not yet complete; otherwise redirects away. */
export function RequireIncompleteProfile() {
  const user = useAuthStore((s) => s.user);

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  if (user?.isProfileComplete) {
    return <Navigate to="/" replace />;
  }
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    element: <RedirectIfAuthenticated />,
    children: [
      {
        path: '/login',
        element: (
          <LazyPage>
            <LoginPage />
          </LazyPage>
        ),
      },
    ],
  },
  {
    element: <RequireIncompleteProfile />,
    children: [
      {
        path: '/complete-profile',
        element: (
          <LazyPage>
            <CompleteProfilePage />
          </LazyPage>
        ),
      },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            index: true,
            element: (
              <LazyPage>
                <HomePage />
              </LazyPage>
            ),
          },
          {
            path: 'members',
            element: (
              <LazyPage>
                <MembersPage />
              </LazyPage>
            ),
          },
          {
            path: 'members/:id',
            element: (
              <LazyPage>
                <MemberDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'capture',
            element: (
              <LazyPage>
                <CapturePage />
              </LazyPage>
            ),
          },
          {
            path: 'archive',
            element: (
              <LazyPage>
                <ArchivePage />
              </LazyPage>
            ),
          },
          {
            path: 'documents/:id',
            element: (
              <LazyPage>
                <DocumentDetailPage />
              </LazyPage>
            ),
          },
          {
            path: 'reports',
            element: (
              <LazyPage>
                <ReportsPage />
              </LazyPage>
            ),
          },
          {
            path: 'profile',
            element: (
              <LazyPage>
                <ProfilePage />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <LazyPage>
        <NotFoundPage />
      </LazyPage>
    ),
  },
]);
